import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { S3Client, PutObjectCommand } from '@aws-sdk/client-s3';
import { environment } from '../../../environments/environment';

export type S3UploadFolder = 'profileImage' | 'familyLogo' | 'memberPhoto';

/** Result returned after a successful S3 upload */
export interface S3UploadResult {
  /** S3 object key — store this in the API payload */
  key: string;
  /** Full public URL for display */
  url: string;
  /** Original file name */
  fileName: string;
}

@Injectable({ providedIn: 'root' })
export class S3ImageUploadService {
  private platformId = inject(PLATFORM_ID);

  private get client(): S3Client {
    return new S3Client({
      region: environment.S3_REGION,
      credentials: {
        accessKeyId: environment.S3_ACCESS_KEY_ID,
        secretAccessKey: environment.S3_SECRET_ACCESS_KEY,
      },
    });
  }

  /** Returns the folder prefix (not bucket) for the given upload type */
  private folderFor(type: S3UploadFolder): string {
    switch (type) {
      case 'profileImage': return environment.S3_BUCKET_FOLDER_PROFILE_IMAGE;
      case 'familyLogo':   return environment.S3_BUCKET_FOLDER_FAMILY_LOGO;
      case 'memberPhoto':  return environment.S3_BUCKET_FOLDER_MEMBER_PHOTO;
    }
  }

  /**
   * Upload a file directly to S3 and return the key + public URL.
   * @param file  The File object from the file input
   * @param folder  Which S3 folder/prefix to upload into
   * @param uniquePrefix  Optional prefix (e.g. userId) to namespace the file
   */
  async upload(
    file: File,
    folder: S3UploadFolder,
    uniquePrefix?: string
  ): Promise<S3UploadResult> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error('S3 upload is only available in the browser.');
    }

    const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
    const timestamp = Date.now();
    const prefix = uniquePrefix ? `${uniquePrefix}/` : '';
    const folderPath = this.folderFor(folder);
    const objectKey = `${folderPath}/${prefix}${timestamp}.${ext}`;

    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    await this.client.send(
      new PutObjectCommand({
        Bucket:             environment.S3_BUCKET_NAME,
        Key:                objectKey,
        Body:               uint8Array,
        ContentType:        file.type || 'image/jpeg',
        ContentDisposition: 'inline',
      })
    );

    return {
      key: objectKey,
      url: this.buildPublicUrl(objectKey),
      fileName: `${timestamp}.${ext}`,
    };
  }

  /** Build a public URL from an object key */
  buildPublicUrl(objectKey: string): string {
    return `https://${environment.S3_BUCKET_NAME}.s3.dualstack.${environment.S3_REGION}.amazonaws.com/${objectKey}`;
  }

  /** Build public URL from folder type + file name */
  buildUrl(folder: S3UploadFolder, fileName: string): string {
    return this.buildPublicUrl(`${this.folderFor(folder)}/${fileName}`);
  }

  isConfigured(): boolean {
    return !!(
      environment.S3_BUCKET_NAME &&
      environment.S3_REGION &&
      environment.S3_ACCESS_KEY_ID &&
      environment.S3_SECRET_ACCESS_KEY
    );
  }
}

